import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { UserPlus, Eye, EyeOff } from 'lucide-react';

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!ageConfirmed) { setError('You must confirm you are 18 years of age or older to create an account.'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true);
    const result = await register(email, password, name);
    if (result.success) {
      navigate('/');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="page-content min-h-screen flex items-center justify-center px-4 bg-[#FDFBEC]">
      <div className="pattern-bg" />
      <div className="neo-card w-full max-w-md p-8 bg-white relative z-10" data-testid="register-form">
        <div className="text-center mb-8">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/ebea1ea5-9731-4354-90db-f3840d8f2692/images/80514a52e5403549c758b4eb7a39bf04c9d4fbd1f3d392449159024de0890e38.png"
            alt="Retro Playground TV"
            className="w-14 h-14 mx-auto mb-3"
          />
          <h1 className="font-['Fredoka'] text-3xl font-black text-[#0A0A0A]">Join Retro Playground TV</h1>
          <p className="text-sm text-[#4B5563] font-['Nunito'] mt-1">Create your free account</p>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg border-2 border-[#FF007F] bg-[#FF007F]/10 text-sm text-[#FF007F] font-bold" data-testid="register-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-bold text-[#0A0A0A] font-['Fredoka'] mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full px-4 py-2.5 border-2 border-[#0A0A0A] rounded-lg font-['Nunito'] focus:outline-none focus:ring-2 focus:ring-[#FF3860]"
              placeholder="Your name"
              data-testid="register-name-input"
            />
          </div>
          <div>
            <label className="block text-sm font-bold text-[#0A0A0A] font-['Fredoka'] mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-2.5 border-2 border-[#0A0A0A] rounded-lg font-['Nunito'] focus:outline-none focus:ring-2 focus:ring-[#FF3860]"
              placeholder="you@example.com"
              data-testid="register-email-input"
            />
          </div>
          <div>
            <label className="block text-sm font-bold text-[#0A0A0A] font-['Fredoka'] mb-1">Password</label>
            <div className="relative">
              <input
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="w-full px-4 py-2.5 pr-10 border-2 border-[#0A0A0A] rounded-lg font-['Nunito'] focus:outline-none focus:ring-2 focus:ring-[#FF3860]"
                placeholder="Min 6 characters"
                data-testid="register-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4B5563]"
              >
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <label className="flex items-start gap-2 cursor-pointer select-none" data-testid="register-age-confirm-label">
            <input
              type="checkbox"
              checked={ageConfirmed}
              onChange={(e) => setAgeConfirmed(e.target.checked)}
              required
              className="mt-1 w-4 h-4 accent-[#FF3860] cursor-pointer"
              data-testid="register-age-checkbox"
            />
            <span className="text-sm text-[#0A0A0A] font-['Nunito'] leading-snug">
              I confirm I am <strong>18 years of age or older</strong>.
            </span>
          </label>
          <button
            type="submit"
            disabled={loading || !ageConfirmed}
            className="neo-btn w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#FF3860] text-white text-base disabled:opacity-50"
            data-testid="register-submit-btn"
          >
            <UserPlus size={18} strokeWidth={2.5} /> {loading ? 'Creating account...' : 'Create Account'}
          </button>
        </form>

        <p className="text-center text-sm text-[#4B5563] font-['Nunito'] mt-6">
          Already have an account?{' '}
          <Link to="/login" className="font-bold text-[#FF3860] hover:underline" data-testid="login-link">
            Sign In
          </Link>
        </p>
      </div>
    </div>
  );
}
